/**
 * Simple Video Player
 *
 * A streamlined video player with click-to-play functionality.
 * Relies on Vimeo's native player controls for all interactions.
 */

class SimpleVideoPlayer {
  constructor(element) {
    console.log('SimpleVideoPlayer constructor called');

    // Core elements
    this.element = element;
    this.mainPlayer = element.querySelector('.video-player-main');
    this.thumbnails = element.querySelectorAll('.video-thumbnail');
    this.thumbnailsWrapper = element.querySelector('.video-thumbnails-wrapper');

    // Video data
    this.videos = [];
    this.currentVideoIndex = 0;

    // Mute state
    this.isMuted = true; // Start muted by default

    // Initialize
    this.init();
  }

  init() {
    console.log('Initializing Simple Video Player');

    // Parse video data from thumbnails
    this.parseVideoData();

    // Set up event listeners
    this.setupThumbnailClickHandlers();
    this.setupKeyboardNavigation();

    // Set initial active state on first thumbnail AND autoplay first video
    if (this.videos.length > 0) {
      this.setActiveThumbnail(0);
      // Autoplay first video on page load
      this.loadVideo(0);
    }

    console.log('Simple Video Player initialization complete');
  }

  parseVideoData() {
    this.videos = Array.from(this.thumbnails).map((thumbnail, index) => {
      const videoType = thumbnail.dataset.videoType;
      const videoId = thumbnail.dataset.videoId;
      const videoSrc = thumbnail.dataset.videoSrc;
      const videoNative = thumbnail.dataset.videoNative;
      const title = thumbnail.dataset.videoTitle || `Video ${index + 1}`;
      const description = thumbnail.dataset.videoDescription || '';
      const loop = thumbnail.dataset.videoLoop === 'true';
      const vimeoHash = thumbnail.dataset.vimeoHash || '';

      return {
        index,
        thumbnail,
        type: videoType,
        id: videoId,
        src: videoSrc,
        native: videoNative ? JSON.parse(videoNative) : null,
        title,
        description,
        loop,
        vimeoHash
      };
    });

    console.log('Parsed video data:', this.videos);
  }

  setupThumbnailClickHandlers() {
    this.thumbnails.forEach((thumbnail, index) => {
      // Click event
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.loadVideo(index);
      });

      // Enter/Space key support
      thumbnail.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.loadVideo(index);
        }
      });
    });
  }

  setupKeyboardNavigation() {
    // Arrow key navigation between thumbnails
    this.thumbnailsWrapper.addEventListener('keydown', (e) => {
      let targetIndex = this.currentVideoIndex;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          targetIndex = Math.max(0, this.currentVideoIndex - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          targetIndex = Math.min(this.videos.length - 1, this.currentVideoIndex + 1);
          break;
        case 'Home':
          e.preventDefault();
          targetIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          targetIndex = this.videos.length - 1;
          break;
        default:
          return;
      }

      if (targetIndex !== this.currentVideoIndex) {
        this.thumbnails[targetIndex].focus();
        this.loadVideo(targetIndex);
      }
    });
  }

  setActiveThumbnail(index) {
    // Update thumbnail states
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
      thumb.setAttribute('aria-selected', i === index);
    });

    this.currentVideoIndex = index;

    // Update description if element exists
    this.updateDescription(index);

    // Scroll active thumbnail into view
    this.scrollToActiveThumbnail();
  }

  updateDescription(index) {
    const descriptionContent = document.getElementById('video-description-content');
    if (descriptionContent && this.videos[index]) {
      const video = this.videos[index];
      const description = video.description || '';

      if (description.trim()) {
        descriptionContent.innerHTML = '<p>' + description + '</p>';
      } else {
        descriptionContent.innerHTML = '<p><em>No description available for this video.</em></p>';
      }
    }
  }

  scrollToActiveThumbnail() {
    const activeThumbnail = this.thumbnails[this.currentVideoIndex];
    if (!activeThumbnail || !this.thumbnailsWrapper) return;

    const containerRect = this.thumbnailsWrapper.getBoundingClientRect();
    const thumbnailRect = activeThumbnail.getBoundingClientRect();

    // Check if thumbnail is out of view
    if (thumbnailRect.left < containerRect.left || thumbnailRect.right > containerRect.right) {
      activeThumbnail.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }

  loadVideo(index) {
    if (index < 0 || index >= this.videos.length) return;

    const video = this.videos[index];
    console.log('Loading video:', video);

    // Set active thumbnail
    this.setActiveThumbnail(index);

    // CRITICAL: Clear main player completely to prevent duplication
    this.mainPlayer.innerHTML = '';

    // Create video element based on type
    let videoElement;

    if (video.type === 'native' && video.native) {
      // Native Shopify video
      videoElement = this.createNativeVideo(video);
      this.mainPlayer.appendChild(videoElement);

      // Start playback
      videoElement.play().catch(error => {
        console.log('Video autoplay failed:', error);
        videoElement.controls = true;
      });

    } else if (video.type === 'youtube' || video.type === 'vimeo') {
      // External video with native controls
      videoElement = this.createExternalVideo(video);
      this.mainPlayer.appendChild(videoElement);
    } else {
      console.error('Unknown video type:', video.type);
    }
  }

  createNativeVideo(video) {
    const videoElement = document.createElement('video');
    videoElement.src = video.native.sources[0].url;
    videoElement.controls = true;
    videoElement.muted = false;
    videoElement.loop = video.loop;
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('preload', 'metadata');
    videoElement.setAttribute('aria-label', video.title);

    if (video.description) {
      videoElement.setAttribute('aria-describedby', `video-desc-${video.index}`);

      // Add hidden description for screen readers
      const desc = document.createElement('div');
      desc.id = `video-desc-${video.index}`;
      desc.className = 'sr-only';
      desc.textContent = video.description;
      this.mainPlayer.appendChild(desc);
    }

    return videoElement;
  }

  createExternalVideo(video) {
    const iframe = document.createElement('iframe');
    let src = video.src;

    console.log('Creating external video:', video.type, src);

    // Ensure Vimeo videos have controls enabled
    if (video.type === 'vimeo') {
      try {
        const url = new URL(src);

        // Always enable native controls
        url.searchParams.set('controls', '1');
        url.searchParams.set('autopause', '0');

        // Add loop if enabled
        if (video.loop) {
          url.searchParams.set('loop', '1');
        }

        // Add privacy hash if present
        if (video.vimeoHash && !url.searchParams.has('h')) {
          url.searchParams.set('h', video.vimeoHash);
        }

        src = url.toString();
        console.log('Final Vimeo URL:', src);
      } catch (error) {
        console.error('Error parsing Vimeo URL:', error);
        // Fallback to basic parameters
        const separator = src.includes('?') ? '&' : '?';
        src += `${separator}controls=1`;
      }
    }

    // Set iframe attributes
    iframe.src = src;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.setAttribute('aria-label', video.title);
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';

    // Error handling
    iframe.addEventListener('error', () => {
      this.handleEmbedError(iframe, video);
    });

    iframe.addEventListener('load', () => {
      console.log(`Video iframe loaded: ${video.title}`);
    });

    return iframe;
  }

  handleEmbedError(iframe, video) {
    console.error(`Failed to embed video: ${video.title} (${video.type}/${video.id})`);

    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'video-embed-error';
    errorDiv.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f9fa;
      color: #6c757d;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      padding: 2rem;
      text-align: center;
      font-size: 0.9rem;
      line-height: 1.4;
    `;

    let errorMessage = 'Unable to load video.';
    if (video.type === 'vimeo') {
      errorMessage += ' This video may be private or require special permissions.';
    }

    errorDiv.innerHTML = `
      <div>
        <div style="margin-bottom: 0.5rem; font-weight: 500;">⚠️ ${errorMessage}</div>
        <div style="font-size: 0.8rem; opacity: 0.8;">
          Video: ${video.title}
        </div>
      </div>
    `;

    // Replace iframe with error message
    iframe.parentNode.replaceChild(errorDiv, iframe);
  }

  // Get current video data
  getCurrentVideoData() {
    return this.videos[this.currentVideoIndex] || null;
  }

  // Update mute state for currently playing video
  updateCurrentVideoMute() {
    const videoElement = this.mainPlayer.querySelector('video, iframe');
    if (!videoElement) return;

    const video = this.getCurrentVideoData();
    if (!video) return;

    if (videoElement.tagName === 'VIDEO') {
      // Native video
      videoElement.muted = this.isMuted;
      console.log('Native video mute state updated:', this.isMuted);
    } else if (videoElement.tagName === 'IFRAME') {
      // External video (YouTube or Vimeo)
      this.updateExternalVideoMute(videoElement);
    }
  }

  // Route mute commands to appropriate platform
  updateExternalVideoMute(iframe) {
    const video = this.getCurrentVideoData();
    if (!video) return;

    if (video.type === 'vimeo') {
      this.updateVimeoMute(iframe, video);
    } else if (video.type === 'youtube') {
      this.updateYouTubeMute(iframe);
    }
  }

  // Update Vimeo video mute state using Player API
  updateVimeoMute(iframe, videoData) {
    // Try modern Vimeo Player API first
    if (window.Vimeo && window.Vimeo.Player) {
      try {
        if (iframe.vimeoPlayer) {
          // Use existing player instance
          iframe.vimeoPlayer.setMuted(this.isMuted).then(() => {
            console.log('Vimeo mute state updated via Player API:', this.isMuted);
          }).catch(error => {
            console.warn('Vimeo Player API mute failed, trying postMessage:', error);
            this.updateVimeoMutePostMessage(iframe);
          });
          return;
        } else {
          // Create new player instance
          const player = new window.Vimeo.Player(iframe);
          iframe.vimeoPlayer = player;

          player.ready().then(() => {
            return player.setMuted(this.isMuted);
          }).then(() => {
            console.log('Vimeo mute state updated via Player API (new instance):', this.isMuted);

            // Register with mute button manager if available
            if (window.muteButtonManager) {
              window.muteButtonManager.registerVimeoPlayer(iframe, player);
            }
          }).catch(error => {
            console.warn('Vimeo Player API initialization failed, trying postMessage:', error);
            this.updateVimeoMutePostMessage(iframe);
          });
          return;
        }
      } catch (error) {
        console.warn('Vimeo Player API not available, trying postMessage:', error);
      }
    }

    // Fallback to postMessage
    this.updateVimeoMutePostMessage(iframe);
  }

  // Fallback Vimeo mute using postMessage API
  updateVimeoMutePostMessage(iframe) {
    try {
      const command = {
        method: this.isMuted ? 'setVolume' : 'setVolume',
        value: this.isMuted ? 0 : 1
      };
      iframe.contentWindow.postMessage(JSON.stringify(command), '*');
      console.log('Vimeo mute command sent via postMessage:', command);
    } catch (error) {
      console.error('Failed to send Vimeo postMessage:', error);
    }
  }

  // Update YouTube video mute state using postMessage API
  updateYouTubeMute(iframe) {
    try {
      const command = this.isMuted ? '{"event":"command","func":"mute","args":""}' : '{"event":"command","func":"unMute","args":""}';
      iframe.contentWindow.postMessage(command, '*');
      console.log('YouTube mute command sent via postMessage:', this.isMuted ? 'mute' : 'unMute');
    } catch (error) {
      console.error('Failed to send YouTube postMessage:', error);
    }
  }

  // Initialize Vimeo Player API for a video
  initializeVimeoPlayer(iframe, video) {
    if (!window.Vimeo || !window.Vimeo.Player) {
      console.warn('Vimeo Player API not available');
      return;
    }

    try {
      const player = new window.Vimeo.Player(iframe);
      iframe.vimeoPlayer = player;

      player.ready().then(() => {
        console.log('Vimeo Player initialized:', video.title);

        // Set initial mute state
        return player.setMuted(this.isMuted);
      }).then(() => {
        console.log('Vimeo initial mute state set:', this.isMuted);

        // Register with mute button manager if available
        if (window.muteButtonManager) {
          window.muteButtonManager.registerVimeoPlayer(iframe, player);
        }
      }).catch(error => {
        console.error('Vimeo Player initialization error:', error);
      });
    } catch (error) {
      console.error('Failed to create Vimeo Player:', error);
    }
  }
}

/**
 * MuteButtonManager
 *
 * Manages the global mute button for video players.
 * Shows/hides button based on video section visibility.
 * Synchronizes mute state across all video players.
 */
class MuteButtonManager {
  constructor() {
    this.muteButton = document.querySelector('.video-player-mute-button');
    this.activeSections = new Set();
    this.globalMuted = true; // Start muted by default
    this.vimeoPlayers = new Map(); // Cache Vimeo player instances
    this.messageListeners = new Set(); // Track message listeners

    if (this.muteButton) {
      this.setupGlobalMuteButton();
      this.setupVimeoMessageListener();
    }
  }

  setupGlobalMuteButton() {
    this.muteButton.addEventListener('click', () => {
      this.globalMuted = !this.globalMuted;
      this.updateAllSections();
      this.updateMuteButtonDisplay();
      console.log('Global mute toggled:', this.globalMuted);
    });

    this.updateMuteButtonDisplay();
  }

  registerSection(videoPlayer) {
    if (!this.muteButton) return;

    const sectionId = videoPlayer.element.dataset.sectionId || Math.random().toString(36).substr(2, 9);
    videoPlayer.sectionId = sectionId;

    // Setup intersection observer for this section
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.activeSections.add(sectionId);
            this.showMuteButton();
          } else {
            this.activeSections.delete(sectionId);
            if (this.activeSections.size === 0) {
              this.hideMuteButton();
            }
          }
        });
      }, {
        rootMargin: '0px 0px 0px 0px',
        threshold: 0.1
      });

      observer.observe(videoPlayer.element);
    }
  }

  updateAllSections() {
    // Update all video players with the global mute state
    window.videoPlayerInstances?.forEach(player => {
      if (player.isMuted !== this.globalMuted) {
        player.isMuted = this.globalMuted;
        player.updateCurrentVideoMute();
      }
    });

    // Also update any cached Vimeo players
    this.syncVimeoPlayers();
  }

  setupVimeoMessageListener() {
    const messageHandler = (event) => {
      if (!event.data || typeof event.data !== 'string') return;

      try {
        const data = JSON.parse(event.data);

        if (data.event === 'volumechange' && event.origin.includes('vimeo.com')) {
          this.handleVimeoVolumeChange(data, event.source);
        }

        if (data.event === 'ready' && event.origin.includes('vimeo.com')) {
          this.handleVimeoReady(event.source);
        }
      } catch (error) {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', messageHandler);
    this.messageListeners.add(messageHandler);
  }

  handleVimeoVolumeChange(data, iframe) {
    if (data.data && typeof data.data.volume !== 'undefined') {
      const volume = data.data.volume;
      const isMuted = volume === 0;

      if (isMuted !== this.globalMuted) {
        console.log('Vimeo player mute state changed externally:', isMuted);
        this.updateMuteButtonDisplay();
      }
    }
  }

  handleVimeoReady(iframe) {
    try {
      const enableEvents = JSON.stringify({
        method: 'addEventListener',
        value: 'volumechange'
      });
      iframe.postMessage(enableEvents, '*');
    } catch (error) {
      console.warn('Failed to enable Vimeo volume events:', error);
    }
  }

  syncVimeoPlayers() {
    this.vimeoPlayers.forEach((player, iframe) => {
      try {
        player.setMuted(this.globalMuted).catch(error => {
          console.warn('Failed to sync Vimeo player mute state:', error);
          this.vimeoPlayers.delete(iframe);
        });
      } catch (error) {
        console.warn('Vimeo player no longer accessible:', error);
        this.vimeoPlayers.delete(iframe);
      }
    });
  }

  registerVimeoPlayer(iframe, player) {
    this.vimeoPlayers.set(iframe, player);

    player.on('volumechange', (data) => {
      const isMuted = data.volume === 0;
      if (isMuted !== this.globalMuted) {
        console.log('Vimeo player volume changed:', data.volume, 'muted:', isMuted);
      }
    });
  }

  updateMuteButtonDisplay() {
    if (!this.muteButton) return;

    const mutedIcon = this.muteButton.querySelector('.mute-icon.muted');
    const unmutedIcon = this.muteButton.querySelector('.mute-icon.unmuted');

    if (this.globalMuted) {
      mutedIcon.style.display = 'block';
      unmutedIcon.style.display = 'none';
      this.muteButton.setAttribute('aria-label', 'Unmute video');
      this.muteButton.setAttribute('title', 'Unmute');
    } else {
      mutedIcon.style.display = 'none';
      unmutedIcon.style.display = 'block';
      this.muteButton.setAttribute('aria-label', 'Mute video');
      this.muteButton.setAttribute('title', 'Mute');
    }
  }

  showMuteButton() {
    if (!this.muteButton) return;
    this.muteButton.classList.add('visible');
  }

  hideMuteButton() {
    if (!this.muteButton) return;
    this.muteButton.classList.remove('visible');
  }

  destroy() {
    this.messageListeners.forEach(listener => {
      window.removeEventListener('message', listener);
    });
    this.messageListeners.clear();
    this.vimeoPlayers.clear();
  }
}

// Initialize video players when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const videoSections = document.querySelectorAll('.video-player-section');

  // Initialize global mute button manager
  const muteButtonManager = new MuteButtonManager();
  window.muteButtonManager = muteButtonManager; // Make it globally accessible
  window.videoPlayerInstances = [];

  videoSections.forEach(section => {
    try {
      const player = new SimpleVideoPlayer(section);
      window.videoPlayerInstances.push(player);
      muteButtonManager.registerSection(player);
    } catch (error) {
      console.error('Failed to initialize video player:', error);
    }
  });

  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    if (window.muteButtonManager) {
      window.muteButtonManager.destroy();
    }
  });
});

// Export for potential use in other scripts
window.SimpleVideoPlayer = SimpleVideoPlayer;